<template>
    <div class="discover-page" @click="hotTrackSelectorOpen = false">
        <main class="discover-shell">
            <section class="discover-hero">
                <div class="discover-hero__copy">
                    <div class="discover-hero__eyebrow">{{ $tt('发现音乐') }}</div>
                    <h1 class="discover-hero__title">{{ $tt('为你打开今天的旋律') }}</h1>
                    <p class="discover-hero__sub">
                        {{ $tt('从热门歌曲、艺术家和新专辑里，快速找到下一首想听的声音。') }}
                    </p>
                    <div class="discover-hero__actions">
                        <button class="discover-button discover-button--primary" @click.stop="playHotTrack">
                            <i class="fas fa-play"></i>
                            <span>{{ $tt('播放精选') }}</span>
                        </button>
                        <button class="discover-button discover-button--ghost" @click.stop="play(0)">
                            <i class="fas fa-random"></i>
                            <span>{{ $tt('随机来一首') }}</span>
                        </button>
                    </div>
                    <div class="discover-hero__stats">
                        <div class="discover-stat">
                            <span>{{ formatNumber(randomSongs.length) }}</span>
                            <small>{{ $tt('推荐歌曲') }}</small>
                        </div>
                        <div class="discover-stat">
                            <span>{{ formatNumber(hotArtists.length) }}</span>
                            <small>{{ $tt('热门艺术家') }}</small>
                        </div>
                        <div class="discover-stat">
                            <span>{{ formatNumber(recommendAlbums.length) }}</span>
                            <small>{{ $tt('推荐专辑') }}</small>
                        </div>
                    </div>
                </div>

                <div
                    :class="[
                        'discover-hero__feature',
                        hotTrackSelectorOpen ? 'is-selector-open' : ''
                    ]"
                    :style="hotTrackBackgroundStyle"
                    @click.stop="playHotTrack"
                >
                    <button
                        class="discover-hero__selector"
                        :aria-label="$tt('切换精选歌曲')"
                        @click.stop="toggleHotTrackSelector"
                    >
                        <i class="fas fa-sliders-h"></i>
                    </button>
                    <div
                        v-if="hotTrackSelectorOpen"
                        class="discover-selector"
                        @click.stop
                    >
                        <div v-if="randomSongs.length" class="discover-selector__grid">
                            <button
                                v-for="song in randomSongs"
                                :key="song.id"
                                :class="[
                                    'discover-selector__item',
                                    isSelectedHotTrack(song) ? 'is-selected' : ''
                                ]"
                                :disabled="savingHotTrackId === String(song.id)"
                                @click="saveHotTrack(song)"
                            >
                                <img :src="songCoverUrl(song)" :alt="song.title || song.name">
                                <span v-if="isSelectedHotTrack(song)" class="discover-selector__check">
                                    <i class="fas fa-check"></i>
                                </span>
                                <span v-if="savingHotTrackId === String(song.id)" class="discover-selector__saving">
                                    <i class="fas fa-spinner fa-spin"></i>
                                </span>
                            </button>
                        </div>
                        <div v-else class="discover-selector__empty">
                            {{ $tt('暂无可选歌曲') }}
                        </div>
                    </div>
                    <div class="discover-hero__scrim"></div>
                    <div class="discover-hero__track">
                        <div class="discover-hero__artist">{{ hotTrack.artist_name || $tt('精选推荐') }}</div>
                        <h2>{{ hotTrack.name || $tt('随机播放') }}</h2>
                        <p>{{ $tt('已播放 {count} 次', { count: formatNumber(hotTrack.play_count || 0) }) }}</p>
                    </div>
                    <button class="discover-hero__play" @click.stop="playHotTrack">
                        <i class="fas fa-play"></i>
                    </button>
                </div>
            </section>

            <section v-if="isLoading" class="discover-state">
                <div class="discover-loading"></div>
                <p>{{ $tt('正在整理推荐...') }}</p>
            </section>

            <div v-else class="discover-layout">
                <div class="discover-main">
                    <section class="discover-section">
                        <div class="discover-section__header">
                            <h2>{{ $tt('热门艺术家') }}</h2>
                            <button class="discover-link" @click="redirectToArtists">{{ $tt('查看全部') }}</button>
                        </div>
                        <div v-if="visibleHotArtists.length" class="discover-artist-strip">
                            <button
                                v-for="artist in visibleHotArtists"
                                :key="artist.id || artist.name"
                                class="discover-artist-card"
                                @click="redirectToArtist(artist.id)"
                            >
                                <img :src="artistCoverUrl(artist)" :alt="artist.name">
                                <div class="discover-artist-card__body">
                                    <strong>{{ artist.name }}</strong>
                                    <span>{{ artist.genres || $tt('继续探索') }}</span>
                                </div>
                                <small>{{ formatNumber(artist.play_count || 0) }}</small>
                            </button>
                        </div>
                        <div v-else class="discover-empty">
                            {{ $tt('暂时没有热门艺术家') }}
                        </div>
                    </section>

                    <section class="discover-section">
                        <div class="discover-section__header">
                            <h2>{{ $tt('热门推荐') }}</h2>
                            <button class="discover-link" @click="play(0)">{{ $tt('播放列表') }}</button>
                        </div>
                        <div v-if="randomSongs.length" class="discover-track-list">
                            <div
                                v-for="(song, index) in randomSongs"
                                :key="song.id || `song-${index}`"
                                class="discover-track-row"
                                role="button"
                                tabindex="0"
                                @click="play(index)"
                                @keyup.enter="play(index)"
                            >
                                <span class="discover-track-row__index">{{ index + 1 }}</span>
                                <img
                                    :src="songCoverUrl(song)"
                                    :alt="song.title || song.name"
                                    class="discover-track-row__cover"
                                >
                                <span class="discover-track-row__meta">
                                    <strong>{{ song.title || song.name }}</strong>
                                    <small>{{ song.artist || $tt('未知艺术家') }}</small>
                                </span>
                                <span class="discover-track-row__album">{{ song.album }}</span>
                                <button
                                    class="discover-track-row__fav"
                                    :aria-label="$tt('收藏歌曲')"
                                    @click.stop="$store.commit('toggleFavorite', song)"
                                >
                                    <i :class="[song.starred ? 'fas text-primary' : 'far', 'fa-heart']"></i>
                                </button>
                                <span class="discover-track-row__duration">{{ formatDuration(song.duration) }}</span>
                            </div>
                        </div>
                        <div v-else class="discover-empty">
                            {{ $tt('暂时没有推荐歌曲') }}
                        </div>
                    </section>

                    <section class="discover-section">
                        <div class="discover-section__header">
                            <h2>{{ $tt('推荐专辑') }}</h2>
                            <button class="discover-link" @click="redirectToAlbums">{{ $tt('查看全部') }}</button>
                        </div>
                        <div v-if="visibleRecommendAlbums.length" class="discover-album-grid">
                            <article
                                v-for="albumItem in visibleRecommendAlbums"
                                :key="albumItem.id"
                                class="discover-album-card"
                                @click="redirectToAlbum(albumItem.id)"
                            >
                                <div class="discover-album-card__cover">
                                    <img :src="albumCoverUrl(albumItem)" :alt="albumItem.name">
                                    <button @click.stop="playAlbumNow(albumItem.id)">
                                        <i class="fas fa-play"></i>
                                    </button>
                                </div>
                                <h3>{{ albumItem.name }}</h3>
                                <p>{{ albumItem.artist_name || albumItem.artist || $tt('未知艺术家') }}</p>
                            </article>
                        </div>
                        <div v-else class="discover-empty">
                            {{ $tt('暂时没有推荐专辑') }}
                        </div>
                    </section>

                    <section class="discover-section discover-section--compact">
                        <div class="discover-section__header">
                            <h2>{{ $tt('最新发布') }}</h2>
                            <button class="discover-link" @click="redirectToAlbums">{{ $tt('查看全部') }}</button>
                        </div>
                        <div v-if="visibleNewAlbums.length" class="discover-release-list">
                            <div
                                v-for="albumItem in visibleNewAlbums"
                                :key="albumItem.id"
                                class="discover-release-row"
                                role="button"
                                tabindex="0"
                                @click="redirectToAlbum(albumItem.id)"
                                @keyup.enter="redirectToAlbum(albumItem.id)"
                            >
                                <img :src="albumCoverUrl(albumItem)" :alt="albumItem.name">
                                <span>
                                    <strong>{{ albumItem.name }}</strong>
                                    <small>{{ albumItem.artist_name || $tt('未知艺术家') }} <span v-if="albumItem.year">/ {{ albumItem.year }}</span></small>
                                </span>
                                <button @click.stop="playAlbumNow(albumItem.id)">
                                    <i class="fas fa-play"></i>
                                </button>
                            </div>
                        </div>
                        <div v-else class="discover-empty">
                            {{ $tt('暂时没有最新发布') }}
                        </div>
                    </section>
                </div>

                <aside class="discover-side">
                    <section class="discover-side-card discover-side-card--feature" v-if="album && album.id">
                        <div class="discover-side-card__label">{{ $tt('精选专辑') }}</div>
                        <img :src="callCoverArtUrl(album.cover)" :alt="album.name" @click="redirectToAlbum(album.id)">
                        <h2>{{ album.name }}</h2>
                        <p>{{ album.comment || $tt('适合现在打开的一张专辑。') }}</p>
                        <button class="discover-button discover-button--primary discover-button--full" @click="playAlbumNow(album.id)">
                            <i class="fas fa-play"></i>
                            <span>{{ $tt('播放专辑') }}</span>
                        </button>
                    </section>

                    <section class="discover-side-card">
                        <div class="discover-side-card__header">
                            <h2>{{ $tt('相似艺术家') }}</h2>
                            <button class="discover-link" @click="redirectToArtists">{{ $tt('全部') }}</button>
                        </div>
                        <div v-if="visibleSimilarArtists.length" class="discover-similar-grid">
                            <button
                                v-for="artist in visibleSimilarArtists"
                                :key="artist.id || artist.name"
                                @click="redirectToArtist(artist.id)"
                            >
                                <img :src="artistCoverUrl(artist)" :alt="artist.name">
                                <span>{{ artist.name }}</span>
                            </button>
                        </div>
                        <div v-else class="discover-empty discover-empty--small">
                            {{ $tt('暂无相似艺术家') }}
                        </div>
                    </section>

                    <section class="discover-side-card">
                        <div class="discover-side-card__header">
                            <h2>{{ $tt('最近播放') }}</h2>
                            <button class="discover-link" @click="redirectToRecord">{{ $tt('历史') }}</button>
                        </div>
                        <div v-if="visibleRecentPlays.length" class="discover-recent-list">
                            <button
                                v-for="(track, index) in visibleRecentPlays"
                                :key="track.id || `recent-${index}`"
                                @click="playRecentTrack(index)"
                            >
                                <img :src="callCoverArtUrl(track.coverArt)" :alt="track.title">
                                <span>
                                    <strong>{{ track.title }}</strong>
                                    <small>{{ track.artist || $tt('未知艺术家') }}</small>
                                </span>
                                <small>{{ formatDuration(track.duration) }}</small>
                            </button>
                        </div>
                        <div v-else class="discover-empty discover-empty--small">
                            {{ $tt('暂无最近播放') }}
                        </div>
                    </section>
                </aside>
            </div>
        </main>
    </div>
</template>
<script>
import { getCoverArtUrl, normalizeTrack } from '@/api/apiUrl/subsonic/serializers'

export default {
    name: 'Discover',
    data() {
        return {
            discover: [],
            randomSongs: [],
            hotArtists: [],
            similarArtists: [],
            album: {},
            recommendAlbums: [],
            recentPlays: [],
            newAlbums: [],
            hotTrack: {},
            savingHotTrackId: '',
            hotTrackSelectorOpen: false,
            loadingRandomSongs: true,
            loadingHotArtists: true
        }
    },
    computed: {
        isPlaying() {
            return this.$store.state.common.isPlaying
        },
        tracks() {
            return this.$store.state.common.queue
        },
        queueIndex() {
            return this.$store.state.common.queueIndex
        },
        isLoading() {
            return this.loadingRandomSongs || this.loadingHotArtists
        },
        hotTrackBackgroundStyle() {
            const fallback = this.randomSongs[0] ? this.songCoverUrl(this.randomSongs[0]) : '/static/dist/img/album_null.jpg'
            const cover = this.hotTrack && this.hotTrack.cover ? this.callCoverArtUrl(this.hotTrack.cover) : fallback
            return {
                backgroundImage: `url(${cover})`
            }
        },
        visibleHotArtists() {
            return this.hotArtists.slice(0, 6)
        },
        visibleSimilarArtists() {
            return this.similarArtists.slice(0, 4)
        },
        visibleRecommendAlbums() {
            return this.recommendAlbums.slice(0, 8)
        },
        visibleNewAlbums() {
            return this.newAlbums.slice(0, 5)
        },
        visibleRecentPlays() {
            return this.recentPlays.slice(0, 5)
        }
    },
    created() {
        this.fetchRandomSongs()
        this.fetchHotArtists()
    },
    methods: {
        callCoverArtUrl(coverArt) {
            const url = getCoverArtUrl({ coverArt })
            if (url) {
                return url
            } else {
                return '/static/dist/img/album_null.jpg'
            }
        },
        dragstart(id, event) {
            event.dataTransfer.setData('application/x-track-id', id)
        },
        remove(idx) {
            return this.$store.commit('removeFromQueue', idx)
        },
        clear() {
            return this.$store.commit('clearQueue')
        },
        shuffle() {
            return this.$store.commit('shuffleQueue')
        },
        formatDuration(value) {
            if (!isFinite(value)) {
                return '∞'
            }
            const minutes = Math.floor(value / 60)
            const seconds = Math.floor(value % 60)
            return (minutes < 10 ? '0' : '') + minutes + ':' + (seconds < 10 ? '0' : '') + seconds
        },
        async playNow(id) {
            const playlist = await this.$api.subsonic.getPlaylist(id)
            return this.$store.dispatch('playTrackList', {
                tracks: playlist.tracks
            })
        },
        albumCoverUrl(albumItem) {
            if (albumItem.image) return albumItem.image
            return this.callCoverArtUrl(albumItem.cover || albumItem.coverArt)
        },
        artistCoverUrl(artist) {
            return this.callCoverArtUrl(artist.cover || artist.coverArt)
        },
        songCoverUrl(song) {
            if (song.image) return song.image
            return this.callCoverArtUrl(song.coverArt || song.cover)
        },
        toggleHotTrackSelector() {
            this.hotTrackSelectorOpen = !this.hotTrackSelectorOpen
        },
        fetchDiscover() {
            return this.$api.subsonic.getDiscover().then((res) => {
                this.discover = res.discover
            })
        },
        async fetchRandomSongs() {
            this.loadingRandomSongs = true
            try {
                const response = await this.$api.subsonic.getRandomSongs(8)
                this.randomSongs = response || []
            } catch (error) {
                console.error('获取随机歌曲失败:', error)
                this.randomSongs = []
            } finally {
                this.loadingRandomSongs = false
            }
        },
        formatNumber(num) {
            if (num >= 1000000) {
                return (num / 1000000).toFixed(1) + 'M'
            } else if (num >= 1000) {
                return (num / 1000).toFixed(1) + 'K'
            }
            return num || 0
        },
        async fetchHotArtists() {
            this.loadingHotArtists = true
            try {
                const response = await this.$api.Task.getHotArtists()
                let hotArtists = response.data.hot_artists || []

                if (hotArtists.length > 0 && hotArtists.length < 6) {
                    const lastArtist = hotArtists[hotArtists.length - 1]
                    while (hotArtists.length < 6) {
                        hotArtists.push({ ...lastArtist })
                    }
                }

                this.hotArtists = hotArtists
                this.similarArtists = response.data.similar_artists || []
                this.album = response.data.album || {}
                this.recommendAlbums = response.data.recommend_albums || []
                this.recentPlays = response.data.recent_plays || []
                this.newAlbums = response.data.new_albums || []
                this.hotTrack = response.data.hot_track || {}
            } catch (error) {
                console.error('获取热门艺术家失败:', error)
                this.hotArtists = []
                this.similarArtists = []
                this.album = {}
                this.recommendAlbums = []
                this.recentPlays = []
                this.newAlbums = []
                this.hotTrack = {}
            } finally {
                this.loadingHotArtists = false
            }
        },
        isSelectedHotTrack(song) {
            return this.hotTrack && this.hotTrack.id && String(this.hotTrack.id) === String(song.id)
        },
        async saveHotTrack(song) {
            if (!song || !song.id || this.isSelectedHotTrack(song)) {
                return
            }
            this.savingHotTrackId = String(song.id)
            try {
                const response = await this.$api.Task.saveDiscoverHotTrack({ track_id: song.id })
                if (response.result) {
                    this.hotTrack = response.data.hot_track || {
                        id: song.id,
                        cover: song.coverArt || '',
                        name: song.title,
                        artist_name: song.artist,
                        play_count: song.playCount || song.plays_count || 0,
                        is_custom: true
                    }
                    this.hotTrackSelectorOpen = false
                    this.$myMsg.notify({
                        title: this.$tt('保存成功'),
                        content: this.$tt('最热歌曲已更新'),
                        type: 'success'
                    })
                } else {
                    this.$myMsg.notify({
                        title: this.$tt('保存失败'),
                        content: response.message || this.$tt('请稍后重试'),
                        type: 'error'
                    })
                }
            } catch (error) {
                console.error('保存最热歌曲失败:', error)
                this.$myMsg.notify({
                    title: this.$tt('保存失败'),
                    content: this.$tt('请稍后重试'),
                    type: 'error'
                })
            } finally {
                this.savingHotTrackId = ''
            }
        },
        redirectToArtist(id) {
            this.$router.push({ name: 'artist', params: { id: id } })
        },
        redirectToArtists() {
            this.$router.push({path: '/artists/most-albums/'})
        },
        redirectToAlbum(id) {
            this.$router.push({ name: 'album', params: { id: id } })
        },
        async playAlbumNow(id) {
            const album = await this.$api.subsonic.getAlbumDetails(id)
            return this.$store.dispatch('playTrackList', {
                tracks: album.tracks
            })
        },
        async playHotTrack() {
            if (this.hotTrack && this.hotTrack.id) {
                const song = await this.$api.subsonic.getSong(this.hotTrack.id)
                return this.$store.dispatch('playTrackList', {
                    tracks: [song]
                })
            }
            if (this.randomSongs.length > 0) {
                return this.play(0)
            }
        },
        play(index) {
            if (!this.randomSongs.length) return
            this.$store.dispatch('playTrackList', {
                index,
                tracks: this.randomSongs
            })
        },
        redirectToAlbums() {
            this.$router.push({path: '/albums/recently-added/'})
        },
        playRecentTrack(index) {
            this.$store.dispatch('playTrackList', {
                index,
                tracks: this.recentPlays.map(normalizeTrack)
            })
        },
        redirectToHistory() {
            this.$router.push({ path: '/tracks/recently-played' })
        },
        redirectToRecord() {
            this.$router.push({ name: 'record' })
        },
        addFavouriteAlbum(item) {
            if (item.favourite) {
                this.$api.subsonic.removeFavourite(item.id, 'album').then((res) => {
                    item.favourite = false
                })
            } else {
                this.$api.subsonic.addFavourite(item.id, 'album').then((res) => {
                    item.favourite = true
                })
            }
        }
    }
}
</script>
<style scoped>
.discover-page {
    height: calc(100dvh - 4.75rem);
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    border-radius: 1rem;
    background:
        radial-gradient(circle at 12% 8%, color-mix(in srgb, oklch(var(--p)) 16%, transparent), transparent 28rem),
        radial-gradient(circle at 88% 4%, color-mix(in srgb, oklch(var(--a)) 12%, transparent), transparent 26rem),
        oklch(var(--b2));
    color: oklch(var(--bc));
}

.discover-shell {
    width: min(100%, 1540px);
    margin: 0 auto;
    padding: 1.25rem;
}

.discover-hero {
    display: grid;
    grid-template-columns: minmax(0, 0.92fr) minmax(24rem, 1.08fr);
    gap: 1.2rem;
    align-items: stretch;
}

.discover-hero__copy,
.discover-hero__feature,
.discover-section,
.discover-side-card {
    border: 1px solid color-mix(in srgb, oklch(var(--bc)) 8%, transparent);
    border-radius: 1.5rem;
    box-shadow: 0 18px 46px color-mix(in srgb, oklch(var(--bc)) 10%, transparent);
}

.discover-hero__copy {
    min-height: 26rem;
    padding: clamp(1.25rem, 3vw, 2.3rem);
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    background: color-mix(in srgb, oklch(var(--b1)) 86%, transparent);
    backdrop-filter: blur(18px);
}

.discover-hero__eyebrow,
.discover-side-card__label {
    font-size: 0.82rem;
    font-weight: 800;
    color: color-mix(in srgb, oklch(var(--bc)) 58%, transparent);
}

.discover-hero__title {
    max-width: 11ch;
    margin-top: 0.55rem;
    font-size: clamp(2.6rem, 5.8vw, 5.8rem);
    line-height: 0.94;
    font-weight: 900;
    letter-spacing: -0.04em;
}

.discover-hero__sub {
    max-width: 36rem;
    margin-top: 1.2rem;
    font-size: 1rem;
    line-height: 1.7;
    color: color-mix(in srgb, oklch(var(--bc)) 68%, transparent);
}

.discover-hero__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    margin-top: 1.6rem;
}

.discover-button {
    min-height: 2.9rem;
    border: 0;
    border-radius: 999px;
    padding: 0 1.1rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    font-weight: 800;
    white-space: nowrap;
    transition: transform 180ms ease, background 180ms ease, color 180ms ease;
}

.discover-button:active,
.discover-track-row:active,
.discover-artist-card:active,
.discover-release-row:active {
    transform: translateY(1px) scale(0.99);
}

.discover-button--primary {
    background: oklch(var(--bc));
    color: oklch(var(--b1));
}

.discover-button--ghost {
    background: color-mix(in srgb, oklch(var(--b1)) 76%, transparent);
    color: oklch(var(--bc));
}

.discover-button--full {
    width: 100%;
}

.discover-hero__stats {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.75rem;
    margin-top: 1.75rem;
}

.discover-stat {
    min-width: 0;
    padding: 0.95rem;
    border-radius: 1.1rem;
    background: color-mix(in srgb, oklch(var(--b1)) 64%, transparent);
}

.discover-stat span,
.discover-stat small {
    display: block;
}

.discover-stat span {
    font-size: 1.45rem;
    line-height: 1;
    font-weight: 900;
}

.discover-stat small {
    margin-top: 0.35rem;
    color: color-mix(in srgb, oklch(var(--bc)) 58%, transparent);
}

.discover-hero__feature {
    position: relative;
    min-height: 26rem;
    overflow: hidden;
    isolation: isolate;
    background-size: cover;
    background-position: center;
    cursor: pointer;
}

.discover-hero__feature::before {
    content: "";
    position: absolute;
    inset: 0;
    z-index: -1;
    background: inherit;
    transform: scale(1.04);
    transition: transform 600ms ease;
}

.discover-hero__feature:hover::before {
    transform: scale(1.08);
}

.discover-hero__scrim {
    position: absolute;
    inset: 0;
    background:
        linear-gradient(90deg, rgba(15, 23, 42, 0.76), rgba(15, 23, 42, 0.12)),
        linear-gradient(180deg, rgba(15, 23, 42, 0.04), rgba(15, 23, 42, 0.72));
}

.discover-hero__selector,
.discover-hero__play {
    position: absolute;
    z-index: 3;
    border: 0;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.9);
    color: #111827;
    box-shadow: 0 16px 34px rgba(15, 23, 42, 0.22);
    opacity: 0;
    pointer-events: none;
    transition: opacity 180ms ease, transform 180ms ease, box-shadow 180ms ease;
}

.discover-hero__feature:hover .discover-hero__selector,
.discover-hero__feature:hover .discover-hero__play,
.discover-hero__feature:focus-within .discover-hero__selector,
.discover-hero__feature:focus-within .discover-hero__play,
.discover-hero__feature.is-selector-open .discover-hero__selector,
.discover-hero__feature.is-selector-open .discover-hero__play {
    opacity: 1;
    pointer-events: auto;
}

.discover-hero__selector {
    top: 1rem;
    right: 1rem;
    width: 2.75rem;
    height: 2.75rem;
    transform: translateY(-0.35rem);
}

.discover-hero__feature:hover .discover-hero__selector,
.discover-hero__feature:focus-within .discover-hero__selector,
.discover-hero__feature.is-selector-open .discover-hero__selector {
    transform: translateY(0);
}

.discover-hero__play {
    right: 1.25rem;
    bottom: 1.25rem;
    width: 3.35rem;
    height: 3.35rem;
    font-size: 1rem;
    transform: translateY(0.35rem);
}

.discover-hero__feature:hover .discover-hero__play,
.discover-hero__feature:focus-within .discover-hero__play,
.discover-hero__feature.is-selector-open .discover-hero__play {
    transform: translateY(0);
}

.discover-selector {
    position: absolute;
    top: 4.25rem;
    right: 1rem;
    z-index: 8;
    width: min(23rem, calc(100vw - 3rem));
    padding: 0.8rem;
    border-radius: 1.2rem;
    border: 1px solid color-mix(in srgb, oklch(var(--bc)) 10%, transparent);
    background: color-mix(in srgb, oklch(var(--b1)) 92%, transparent);
    backdrop-filter: blur(18px);
    box-shadow: 0 24px 54px rgba(15, 23, 42, 0.22);
}

.discover-selector__grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.6rem;
}

.discover-selector__item {
    position: relative;
    overflow: hidden;
    border: 2px solid transparent;
    border-radius: 0.8rem;
    background: transparent;
    aspect-ratio: 1;
}

.discover-selector__item.is-selected {
    border-color: oklch(var(--p));
}

.discover-selector__item img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.discover-selector__check,
.discover-selector__saving {
    position: absolute;
    inset: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    background: rgba(15, 23, 42, 0.48);
}

.discover-selector__empty {
    padding: 1.5rem 1rem;
    text-align: center;
    color: color-mix(in srgb, oklch(var(--bc)) 62%, transparent);
}

.discover-hero__track {
    position: absolute;
    left: 1.5rem;
    right: 5.5rem;
    bottom: 1.5rem;
    z-index: 2;
    color: #fff;
}

.discover-hero__artist {
    max-width: 100%;
    display: inline-flex;
    padding: 0.38rem 0.72rem;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.16);
    backdrop-filter: blur(10px);
    font-size: 0.8rem;
    font-weight: 800;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.discover-hero__track h2 {
    max-width: 100%;
    margin-top: 0.8rem;
    font-size: clamp(2.1rem, 4vw, 4.8rem);
    line-height: 0.96;
    font-weight: 900;
    letter-spacing: -0.035em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.discover-hero__track p {
    margin-top: 0.55rem;
    color: rgba(255, 255, 255, 0.78);
}

.discover-state {
    min-height: 18rem;
    margin-top: 1.2rem;
    border-radius: 1.5rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.9rem;
    background: color-mix(in srgb, oklch(var(--b1)) 76%, transparent);
    color: color-mix(in srgb, oklch(var(--bc)) 64%, transparent);
}

.discover-loading {
    width: 2rem;
    height: 2rem;
    border-radius: 999px;
    border: 3px solid color-mix(in srgb, oklch(var(--bc)) 14%, transparent);
    border-top-color: oklch(var(--p));
    animation: discover-spin 800ms linear infinite;
}

.discover-layout {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 23rem;
    gap: 1.2rem;
    align-items: start;
    margin-top: 1.2rem;
    min-width: 0;
}

.discover-main,
.discover-side {
    display: grid;
    gap: 1.2rem;
    min-width: 0;
}

.discover-side {
    position: sticky;
    top: 1rem;
}

.discover-section,
.discover-side-card {
    min-width: 0;
    padding: 1.05rem;
    background: color-mix(in srgb, oklch(var(--b1)) 82%, transparent);
    backdrop-filter: blur(18px);
}

.discover-section__header,
.discover-side-card__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1rem;
}

.discover-section__header h2,
.discover-side-card__header h2 {
    font-size: 1.45rem;
    line-height: 1;
    font-weight: 900;
    letter-spacing: -0.03em;
}

.discover-link {
    border: 0;
    background: transparent;
    color: oklch(var(--p));
    font-weight: 800;
    white-space: nowrap;
}

.discover-artist-strip {
    display: grid;
    grid-template-columns: repeat(6, minmax(8.5rem, 1fr));
    gap: 0.85rem;
    width: 100%;
    min-width: 0;
    overflow-x: auto;
    overflow-y: hidden;
    padding-bottom: 0.2rem;
    scrollbar-width: none;
}

.discover-artist-strip::-webkit-scrollbar {
    display: none;
}

.discover-artist-card {
    min-width: 8.5rem;
    border: 0;
    border-radius: 1.2rem;
    padding: 0.75rem;
    background: color-mix(in srgb, oklch(var(--b2)) 68%, oklch(var(--b1)));
    text-align: left;
    color: oklch(var(--bc));
    transition: transform 180ms ease, background 180ms ease;
}

.discover-artist-card:hover {
    transform: translateY(-2px);
    background: color-mix(in srgb, oklch(var(--b1)) 92%, oklch(var(--p)) 8%);
}

.discover-artist-card img {
    width: 100%;
    aspect-ratio: 1;
    object-fit: cover;
    border-radius: 1rem;
}

.discover-artist-card__body {
    min-width: 0;
    margin-top: 0.75rem;
}

.discover-artist-card strong,
.discover-artist-card span,
.discover-artist-card small {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.discover-artist-card strong {
    font-weight: 900;
}

.discover-artist-card span,
.discover-artist-card small {
    color: color-mix(in srgb, oklch(var(--bc)) 56%, transparent);
}

.discover-artist-card small {
    margin-top: 0.5rem;
}

.discover-track-list {
    display: grid;
    gap: 0.55rem;
}

.discover-track-row,
.discover-release-row,
.discover-recent-list button {
    width: 100%;
    min-width: 0;
    border: 0;
    border-radius: 1.1rem;
    background: color-mix(in srgb, oklch(var(--b1)) 78%, transparent);
    color: oklch(var(--bc));
    transition: transform 180ms ease, background 180ms ease, box-shadow 180ms ease;
}

.discover-track-row {
    display: grid;
    grid-template-columns: 2.1rem 3.35rem minmax(0, 1.1fr) minmax(0, 0.8fr) 2.4rem 4.5rem;
    align-items: center;
    gap: 0.85rem;
    padding: 0.55rem 0.7rem;
    text-align: left;
}

.discover-track-row:hover,
.discover-release-row:hover,
.discover-recent-list button:hover {
    background: color-mix(in srgb, oklch(var(--b1)) 92%, oklch(var(--p)) 8%);
    box-shadow: 0 14px 30px color-mix(in srgb, oklch(var(--bc)) 8%, transparent);
}

.discover-track-row__index {
    color: color-mix(in srgb, oklch(var(--bc)) 46%, transparent);
    font-weight: 800;
    text-align: center;
}

.discover-track-row__cover {
    width: 3.35rem;
    height: 3.35rem;
    object-fit: cover;
    border-radius: 0.9rem;
}

.discover-track-row__meta,
.discover-track-row__album,
.discover-release-row span,
.discover-recent-list span {
    min-width: 0;
}

.discover-track-row__meta strong,
.discover-track-row__meta small,
.discover-track-row__album,
.discover-release-row strong,
.discover-release-row small,
.discover-recent-list strong,
.discover-recent-list small {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.discover-track-row__meta strong,
.discover-release-row strong,
.discover-recent-list strong {
    font-weight: 900;
}

.discover-track-row__meta small,
.discover-track-row__album,
.discover-release-row small,
.discover-recent-list small,
.discover-track-row__duration {
    color: color-mix(in srgb, oklch(var(--bc)) 56%, transparent);
}

.discover-track-row__fav,
.discover-release-row > button {
    width: 2.35rem;
    height: 2.35rem;
    border: 0;
    border-radius: 999px;
    background: color-mix(in srgb, oklch(var(--b2)) 72%, transparent);
    color: color-mix(in srgb, oklch(var(--bc)) 66%, transparent);
}

.discover-track-row__duration {
    text-align: right;
    font-weight: 800;
}

.discover-album-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.9rem;
}

.discover-album-card {
    min-width: 0;
    cursor: pointer;
}

.discover-album-card__cover {
    position: relative;
    overflow: hidden;
    border-radius: 1.1rem;
    background: oklch(var(--b2));
}

.discover-album-card__cover img {
    width: 100%;
    aspect-ratio: 1;
    object-fit: cover;
    transition: transform 420ms ease;
}

.discover-album-card:hover img {
    transform: scale(1.05);
}

.discover-album-card__cover button {
    position: absolute;
    right: 0.7rem;
    bottom: 0.7rem;
    width: 2.65rem;
    height: 2.65rem;
    border: 0;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.92);
    color: #111827;
    opacity: 0;
    transform: translateY(0.4rem);
    transition: opacity 180ms ease, transform 180ms ease;
}

.discover-album-card:hover button {
    opacity: 1;
    transform: translateY(0);
}

.discover-album-card h3,
.discover-album-card p {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.discover-album-card h3 {
    margin-top: 0.65rem;
    font-weight: 900;
}

.discover-album-card p {
    color: color-mix(in srgb, oklch(var(--bc)) 58%, transparent);
}

.discover-release-list,
.discover-recent-list {
    display: grid;
    gap: 0.55rem;
}

.discover-release-row {
    display: grid;
    grid-template-columns: 4rem minmax(0, 1fr) 2.4rem;
    align-items: center;
    gap: 0.8rem;
    padding: 0.65rem;
    text-align: left;
}

.discover-release-row img {
    width: 4rem;
    height: 4rem;
    object-fit: cover;
    border-radius: 0.95rem;
}

.discover-side-card--feature > img {
    width: 100%;
    aspect-ratio: 1;
    margin-top: 0.85rem;
    object-fit: cover;
    border-radius: 1.2rem;
    cursor: pointer;
}

.discover-side-card--feature h2 {
    margin-top: 0.85rem;
    font-size: 1.45rem;
    line-height: 1.08;
    font-weight: 900;
}

.discover-side-card--feature p {
    margin: 0.45rem 0 0.9rem;
    color: color-mix(in srgb, oklch(var(--bc)) 62%, transparent);
}

.discover-similar-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.65rem;
}

.discover-similar-grid button {
    min-width: 0;
    border: 0;
    border-radius: 1rem;
    padding: 0.7rem;
    background: color-mix(in srgb, oklch(var(--b2)) 70%, transparent);
    color: oklch(var(--bc));
}

.discover-similar-grid img {
    width: 100%;
    aspect-ratio: 1;
    object-fit: cover;
    border-radius: 999px;
}

.discover-similar-grid span {
    display: block;
    margin-top: 0.55rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 800;
}

.discover-recent-list button {
    display: grid;
    grid-template-columns: 3.1rem minmax(0, 1fr) 3.5rem;
    align-items: center;
    gap: 0.7rem;
    padding: 0.55rem;
    text-align: left;
}

.discover-recent-list img {
    width: 3.1rem;
    height: 3.1rem;
    object-fit: cover;
    border-radius: 0.85rem;
}

.discover-recent-list > button > small {
    text-align: right;
}

.discover-empty {
    display: flex;
    min-height: 8rem;
    align-items: center;
    justify-content: center;
    border-radius: 1.2rem;
    background: color-mix(in srgb, oklch(var(--b2)) 64%, transparent);
    color: color-mix(in srgb, oklch(var(--bc)) 58%, transparent);
    font-weight: 700;
}

.discover-empty--small {
    min-height: 5.5rem;
}

@keyframes discover-spin {
    to {
        transform: rotate(360deg);
    }
}

@media (prefers-reduced-motion: reduce) {
    .discover-page *,
    .discover-page *::before,
    .discover-page *::after {
        animation: none !important;
        transition: none !important;
    }
}

@media (max-width: 1180px) {
    .discover-hero,
    .discover-layout {
        grid-template-columns: 1fr;
    }

    .discover-side {
        position: static;
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .discover-side-card--feature {
        grid-column: span 1;
    }
}

@media (max-width: 900px) {
    .discover-shell {
        padding: 0.8rem;
    }

    .discover-hero {
        gap: 0.9rem;
    }

    .discover-hero__copy,
    .discover-hero__feature {
        min-height: 21rem;
    }

    .discover-layout,
    .discover-main,
    .discover-side {
        gap: 0.9rem;
    }

    .discover-album-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .discover-side {
        grid-template-columns: 1fr;
    }

    .discover-track-row {
        grid-template-columns: 2rem 3.2rem minmax(0, 1fr) 2.35rem 4.2rem;
    }

    .discover-track-row__album {
        display: none;
    }
}

@media (max-width: 640px) {
    .discover-page {
        height: calc(100dvh - 12.25rem);
        border-radius: 0;
        background:
            radial-gradient(circle at 18% 0%, rgba(191, 219, 254, 0.42), transparent 18rem),
            #f7f6f3;
        color: #111827;
    }

    .discover-shell {
        padding: 0.75rem 0.45rem 0.35rem;
    }

    .discover-hero__copy {
        min-height: auto;
        border: 0;
        box-shadow: none;
        padding: 0.55rem 0.25rem 0.2rem;
        background: transparent;
        backdrop-filter: none;
    }

    .discover-hero__eyebrow {
        color: #94a3b8;
        font-size: 0.9rem;
    }

    .discover-hero__title {
        max-width: 9ch;
        font-size: 2.1rem;
        color: #111827;
    }

    .discover-hero__sub {
        max-width: 20rem;
        color: #64748b;
        font-size: 0.92rem;
    }

    .discover-hero__feature {
        min-height: 13rem;
        border-radius: 1.75rem;
    }

    .discover-hero__stats {
        grid-template-columns: 1fr;
    }

    .discover-stat,
    .discover-section,
    .discover-side-card,
    .discover-track-row,
    .discover-release-row,
    .discover-recent-list button {
        background: rgba(255, 255, 255, 0.76);
        box-shadow: 0 16px 34px rgba(148, 163, 184, 0.14);
    }

    .discover-section,
    .discover-side-card {
        border: 0;
        border-radius: 1.5rem;
        padding: 0.9rem;
    }

    .discover-section__header h2,
    .discover-side-card__header h2 {
        font-size: 1.45rem;
        color: #111827;
    }

    .discover-artist-strip {
        grid-template-columns: repeat(6, minmax(8rem, 1fr));
    }

    .discover-track-row {
        grid-template-columns: 3rem minmax(0, 1fr) 2.35rem;
        padding: 0.75rem;
        border-radius: 1.25rem;
    }

    .discover-track-row__index,
    .discover-track-row__duration {
        display: none;
    }

    .discover-track-row__cover {
        width: 3rem;
        height: 3rem;
    }

    .discover-album-grid {
        gap: 0.75rem;
    }

    .discover-album-card__cover button {
        opacity: 1;
        transform: none;
    }

    .discover-release-row {
        grid-template-columns: 3.7rem minmax(0, 1fr) 2.4rem;
    }

    .discover-release-row img {
        width: 3.7rem;
        height: 3.7rem;
    }
}
</style>
